import { Pencil, Repeat, X } from 'lucide-react';
import { Button, Card, Chip } from '@heroui/react';
import type { AvailabilityWindow } from '../../../api/my-parking-spots';
import { DateTimeFrame } from '../../../components/ui/datetime-frame';
import { UsageTypeChip } from '../../../components/ui/usage-chips';
import { useRecurringAvailabilityEnabled } from '../../../hooks/useFeatureFlags';
import { formatDateBR, formatTimeBR } from '../../../utils/date-utils';

interface ActiveWindowRowProps {
  window: AvailabilityWindow;
  isHighlighted?: boolean;
  onHighlight?: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

export function ActiveWindowRow({
  window,
  isHighlighted = false,
  onHighlight,
  onEdit,
  onCancel,
}: ActiveWindowRowProps) {
  const isPressable = Boolean(onHighlight);
  const showSeries = useRecurringAvailabilityEnabled() && Boolean(window.seriesId);

  return (
    <Card
      className={[
        isHighlighted ? 'ring-2 ring-accent' : '',
        isPressable ? 'cursor-pointer transition-colors hover:bg-surface-secondary/60' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Card.Content
        className="flex flex-col gap-3 p-4"
        role={isPressable ? 'button' : undefined}
        tabIndex={isPressable ? 0 : undefined}
        aria-pressed={isPressable ? isHighlighted : undefined}
        aria-label={
          isPressable
            ? `Janela de ${formatDateBR(window.startAt)} ${formatTimeBR(window.startAt)} até ${formatDateBR(window.endAt)} ${formatTimeBR(window.endAt)}`
            : undefined
        }
        onClick={onHighlight}
        onKeyDown={
          isPressable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onHighlight?.();
                }
              }
            : undefined
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <DateTimeFrame label="Início" iso={window.startAt} />
          <DateTimeFrame label="Fim" iso={window.endAt} />
        </div>

        {window.allowBorrow || window.allowRent || showSeries ? (
          <div className="flex flex-wrap gap-1.5">
            {showSeries ? (
              <Chip size="sm" variant="soft" color="accent" className="inline-flex items-center gap-1">
                <Repeat size={12} />
                Recorrente
              </Chip>
            ) : null}
            {window.allowBorrow ? <UsageTypeChip type="BORROW" /> : null}
            {window.allowRent ? <UsageTypeChip type="RENT" /> : null}
          </div>
        ) : null}

        <div
          className="flex gap-2"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Button size="sm" variant="primary" className="flex-1" onPress={onEdit}>
            <Pencil size={14} />
            Editar
          </Button>
          <Button size="sm" variant="danger" className="flex-1" onPress={onCancel}>
            <X size={14} />
            Cancelar
          </Button>
        </div>
      </Card.Content>
    </Card>
  );
}
